/**
 * Horizontal pill-style tab strip with animated selection indicator,
 * scroll-into-view, and fade masks. Wraps React Aria Tabs.
 *
 * Panels are optional — if no tab has `content`, only the tab list renders
 * (useful for controlling external content via `onSelectionChange`).
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  Tabs as AriaTabs,
  TabList as AriaTabList,
  Tab as AriaTab,
  TabPanel as AriaTabPanel,
  type Key,
} from 'react-aria-components';
import { motion } from 'motion/react';
import { cn } from '@/foundation/utils/cn';

export interface TabItem {
  id: string;
  label: string;
  content?: ReactNode | (() => ReactNode);
  isDisabled?: boolean;
}

interface TabsProps {
  tabs?: TabItem[];
  selectedKey?: string;
  onSelectionChange?: (key: string) => void;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  listClassName?: string;
  tabClassName?: string;
  panelClassName?: string;
}

interface ScrollState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

const FADE_SIZE = 40;

function getMaskStyle({ canScrollLeft, canScrollRight }: ScrollState): CSSProperties | undefined {
  if (!canScrollLeft && !canScrollRight) return undefined;

  let mask: string;
  if (canScrollLeft && canScrollRight) {
    mask = `linear-gradient(to right, transparent, black ${FADE_SIZE}px, black calc(100% - ${FADE_SIZE}px), transparent)`;
  } else if (canScrollLeft) {
    mask = `linear-gradient(to right, transparent, black ${FADE_SIZE}px)`;
  } else {
    mask = `linear-gradient(to left, transparent, black ${FADE_SIZE}px)`;
  }

  return { maskImage: mask, WebkitMaskImage: mask };
}

export function Tabs({
  tabs = [],
  selectedKey: controlledKey,
  onSelectionChange,
  orientation = 'horizontal',
  className,
  listClassName,
  tabClassName,
  panelClassName,
}: TabsProps) {
  const pillId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  // Track selection for pill animation (works for both controlled + uncontrolled)
  const [internalKey, setInternalKey] = useState(tabs[0]?.id);
  const activeKey = controlledKey ?? internalKey;

  const handleSelectionChange = useCallback(
    (key: Key) => {
      setInternalKey(String(key));
      onSelectionChange?.(String(key));
    },
    [onSelectionChange]
  );

  // ── Scroll state for fade masks ────────────────────────────────────────────

  const [scrollState, setScrollState] = useState<ScrollState>({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setScrollState({
      canScrollLeft: el.scrollLeft > 1,
      canScrollRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  // ── Scroll selected tab into view ──────────────────────────────────────────

  useEffect(() => {
    if (!activeKey) return;
    const list = listRef.current;
    if (!list) return;

    requestAnimationFrame(() => {
      const tab = list.querySelector<HTMLElement>('[data-selected]');
      if (!tab) return;

      const listRect = list.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();

      const isFullyVisible = tabRect.left >= listRect.left && tabRect.right <= listRect.right;
      if (isFullyVisible) return;

      const tabCenter = tab.offsetLeft + tab.offsetWidth / 2;
      const targetScroll = tabCenter - list.clientWidth / 2;
      const startScroll = list.scrollLeft;
      const delta = targetScroll - startScroll;
      const duration = 350;
      let start: number | null = null;

      const step = (timestamp: number) => {
        if (!start) start = timestamp;
        const t = Math.min((timestamp - start) / duration, 1);
        const ease = t * (2 - t); // ease-out
        list.scrollLeft = startScroll + delta * ease;
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }, [activeKey]);

  if (!Array.isArray(tabs) || tabs.length === 0) return null;

  const hasPanels = tabs.some((t) => t.content !== undefined);

  return (
    <AriaTabs
      selectedKey={activeKey}
      onSelectionChange={handleSelectionChange}
      orientation={orientation}
      className={className}
    >
      <AriaTabList
        ref={listRef}
        className={cn('flex gap-1.5 overflow-x-auto scrollbar-none -mx-1 px-1', listClassName)}
        style={getMaskStyle(scrollState)}
      >
        {tabs.map((t) => {
          const isActive = t.id === activeKey;

          return (
            <AriaTab
              key={t.id}
              id={t.id}
              isDisabled={t.isDisabled}
              className={cn(
                'appearance-none border-0 bg-transparent relative shrink-0 px-3.5 py-1.5 cursor-pointer rounded-full whitespace-nowrap outline-none',
                'text-sm font-semibold transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--f-accent)]',
                isActive
                  ? 'text-[var(--f-accent)]'
                  : 'text-[var(--f-fg-muted)] hover:text-[var(--f-fg-secondary)]',
                t.isDisabled && 'opacity-50 cursor-not-allowed',
                tabClassName
              )}
            >
              {/* Dual animated pill — first layer springs fast, second catches up */}
              {isActive && (
                <motion.div
                  layoutId={`tab-pill-bg-${pillId}`}
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'color-mix(in srgb, var(--f-accent) 10%, transparent)',
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              {isActive && (
                <motion.div
                  layoutId={`tab-pill-ring-${pillId}`}
                  className="absolute inset-0 rounded-full"
                  style={{
                    border: '1.5px solid color-mix(in srgb, var(--f-accent) 25%, transparent)',
                  }}
                  transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                />
              )}
              <span className="relative">{t.label}</span>
            </AriaTab>
          );
        })}
      </AriaTabList>

      {hasPanels &&
        tabs.map((tab) => (
          <AriaTabPanel
            key={tab.id}
            id={tab.id}
            className={cn('pt-3 outline-none', panelClassName)}
          >
            {tab.content !== undefined
              ? typeof tab.content === 'function'
                ? tab.content()
                : tab.content
              : null}
          </AriaTabPanel>
        ))}
    </AriaTabs>
  );
}
